import { IUserIdentity } from './IUserIdentity';
import * as eftAuthenApi from 'cd-eft/services/eftAuthenApi';

export class EftUserIdentityProvider implements IUserIdentity {
  private _username: string | undefined;
  private _userRoleId: number | undefined;
  private _accessToken: string | undefined;
  private _expired: boolean;
  private _expireHandler: (() => void) | undefined;

  constructor() {
    // read current values from localStorage
    this._username = localStorage['username'];
    this._userRoleId = undefined;
    this._accessToken = localStorage['accessToken'];
    this._expired = false;
    this._expireHandler = undefined;
  }

  get username(): string | undefined {
    // ensure observable matches localstorage (could have changed out of process)
    this._username = localStorage['username'];
    return this._username;
  }

  get userRoleId(): number | undefined {
    return this._userRoleId;
  }

  get accessToken(): string | undefined {
    // ensure observable matches localstorage (could have changed out of process)
    this._accessToken = localStorage['accessToken'];
    return this._accessToken;
  }

  get expired(): boolean {
    return this._expired;
  }

  get expireHandler(): (() => void) | undefined {
    return this._expireHandler;
  }

  async logout(): Promise<void> {
    try {
      if (this.username) await eftAuthenApi.revokeAccessToken(this.username);
    } catch {
      //ignore non-critical error
    } finally {
      // update localStorage
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');

      // update observables
      this._accessToken = localStorage['accessToken'];
      this._username = localStorage['username'];
      this._userRoleId = undefined;
      this._expired = false;
    }
  }

  async login(username: string, password: string): Promise<void> {
    // revoke any current login
    try {
      if (this.username) await eftAuthenApi.revokeAccessToken(this.username);
    } catch {
      //ignore non-critical error
    } finally {
      // update localStorage
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');
    }

    // authenticate
    try {
      const data = await eftAuthenApi.generateAccessToken(username, password);

      if (!data) throw new Error('Unexpected result.');
      if (!data.accessToken) throw new Error('Unexpected result.');
      if (!data.refreshToken) throw new Error('Unexpected result.');
      if (!data.systemAdminRoleId) throw new Error('Unexpected result.');

      // update localStorage
      localStorage['username'] = username;
      this._userRoleId = 4;// In SFI envs, just give Dev role
      localStorage['accessToken'] = data.accessToken;
      localStorage['refreshToken'] = data.refreshToken;
      this._expired = false;
    } finally {
      // update observables
      this._username = localStorage['username'];
      this._accessToken = localStorage['accessToken'];
    }
  }

  async refresh(): Promise<void> {
    const refreshToken = localStorage['refreshToken'];
    try {
      const data = await eftAuthenApi.extendAccessToken(refreshToken);

      if (!data) throw new Error('Unexpected result.');
      if (!data.accessToken) throw new Error('Unexpected result.');
      if (!data.systemAdminRoleId) throw new Error('Unexpected result.');

      localStorage['accessToken'] = data.accessToken;
      this._accessToken = data.accessToken;
      this._userRoleId = 4;// In SFI envs, just give Dev role
      this._expired = false;
    } catch (error) {
      this._expired = true;
      console.warn('REFRESH TOKEN EXPIRED OR INVALID');

      // re-throw the error
      throw new Error('REFRESH TOKEN EXPIRED OR INVALID');
    }
  }

  addExpireHandler(callback: () => void) {
    this._expireHandler = callback;
  }
}
